import React from 'react';
import { useFlowConfig } from './useFlowConfig';
import { FlowDeviceConfig } from './useFlowConfig';
import {
  Factory,
  Heater,
  Siren,
  ThermometerSnowflake,
  ThermometerSun,
  TriangleAlert,
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'components/ui/accordion';
import FlowInternalHistory from '../components/FlowInternalHistory';
import { getBaseAlertMessageString, getExtendedAlertMessageString } from './helpers';

interface AlertType {
  type: 'freeze' | 'lowLevel' | 'heat' | 'plantAlerts';
  message: string;
}

function getAlertTypesWithThresholds(flowConfig: FlowDeviceConfig): AlertType[] {
  const alertTypes: AlertType[] = [];
  if (flowConfig.temp?.alert) {
    alertTypes.push({
      type: 'freeze',
      message: getBaseAlertMessageString(flowConfig, 'tempAlert'),
    });
  }
  if (flowConfig.cmLimit) {
    alertTypes.push({
      type: 'lowLevel',
      message: getBaseAlertMessageString(flowConfig, 'belowLimit'),
    });
  }
  if (flowConfig.temp?.alertMax) {
    alertTypes.push({
      type: 'heat',
      message: getBaseAlertMessageString(flowConfig, 'tempAlertMax'),
    });
  }
  if (flowConfig.plantAlerts) {
    alertTypes.push({
      type: 'plantAlerts',
      message: getBaseAlertMessageString(flowConfig, 'plantAlerts'),
    });
  }
  return alertTypes;
}

function getAlertIconByType(type: AlertType['type']) {
  switch (type) {
    case 'freeze':
      return <ThermometerSnowflake className="w-5 h-5 text-yellow-500" />;
    case 'lowLevel':
      return <TriangleAlert className="w-5 h-5 text-yellow-500" />;
    case 'heat':
      return <Heater className="w-5 h-5 text-red-500" />;
    case 'plantAlerts':
      return <Factory className="w-5 h-5 text-red-500" />;
    default:
      return <Siren />;
  }
}

import LevelAlertsConfigModal from './LevelAlertsConfigModal';
import { Settings } from 'lucide-react';
import { Button } from 'components/ui/button';

export default function LevelConfig({ deviceId }: { deviceId: string }) {
  const { data: flowConfig } = useFlowConfig(deviceId);
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  // if no flowConfig, return null
  if (!flowConfig) {
    return null;
  }
  const alertTypes = getAlertTypesWithThresholds(flowConfig);

  // Determine if we should show the accordion.
  // We want to show the "Alarmer" section if there are alerts OR if we want to allow configuring them.
  // The user requested to "add to the UI a modal to toggle these alerts on and off".
  // So we should probably always show the section or at least the header to access settings?
  // Or maybe just show a button to configure?
  // Existing code returns null if no alertTypes.
  // I will change it to always render the Accordion but maybe show "Ingen alarmer" text if empty,
  // AND add the settings button in the trigger or separate.
  // Actually, let's keep the Accordion for listing ACTIVE alerts,
  // but if list is empty, we might still want to show the configuration option.
  // The user said "add to the UI a modal...".
  // Let's modify the UI to always show the section "Alarmer / Konfiguration" or similar.

  return (
    <>
      <Accordion type="single" collapsible className="w-full bg-white border rounded-lg">
        <AccordionItem value="alerts" className="border-0 ">
          <AccordionTrigger className="py-3 px-4 hover:no-underline bg-gray-50  hover:bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between w-full pr-4">
              <div className="flex items-center gap-2">
                <Siren className="w-4 h-4 text-yellow-500" />
                <span className="text-sm font-medium text-gray-700">
                  Alarmer ({alertTypes.length > 0 ? 'Til' : 'Fra'})
                </span>
              </div>
              <div
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsModalOpen(true);
                }}
                className="p-1 hover:bg-gray-200 rounded-full transition-colors"
              >
                <Settings className="w-4 h-4 text-gray-500" />
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-0">
            {alertTypes.length > 0 ? (
              <ul className="space-y-4 py-2 px-4">
                {alertTypes.map((alertType) => {
                  const icon = getAlertIconByType(alertType.type);
                  return (
                    <li key={alertType.type}>
                      <div className="flex items-center">
                        {icon}
                        <span className="ml-2 text-sm text-gray-600">{alertType.message}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="py-4 px-4 text-sm text-gray-500 text-center">
                Ingen aktive alarmer. Klik på tandhjulet for at konfigurere.
              </div>
            )}
            <FlowInternalHistory configId={flowConfig.id} variant="plant" />
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {isModalOpen && (
        <LevelAlertsConfigModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          config={flowConfig}
        />
      )}
    </>
  );
}
